// DashboardModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useDashboardModalState from './useDashboardModalState';

// CSS
import './DashboardModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import Switch from 'components/switch/Switch';
import TextInput from 'components/textinput/TextInput';

/**
 * DashboardModal
 *
 * Set dashboard title and metadata when adding a dashboard, or edit an existing dashboard.
 *
 * @returns {ReactNode} The rendered component.
 */
const DashboardModal = React.memo(({
    isOpen,
    onClose,
    onCreated,
    dashboard = null,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- HOOKS ----- //

    const {
        modalState,
        formState,
        handlers,
    } = useDashboardModalState({
        isOpen,
        onClose,
        onCreated,
        dashboard,
    });

    // ----- RENDER ----- //

    return (
        <Modal
          iconKey="dashboard"
          title="Dashboard Settings"
          isOpen={isOpen}
          width={isMobile ? '100%' : 520}
          height='auto'
          onClose={handlers.handleClose}
        >
            <ModalForm>

                <ModalField>

                    <ModalLabel label="Title" />

                    <TextInput
                      value={formState.title}
                      placeholder="Dashboard title"
                      autoFocus
                      onChange={(event) => formState.setTitle(event.target.value)}
                    />

                    <ModalHint hint="Give your dashboard a descriptive title to distinguish it for viewers." />

                </ModalField>

                <ModalField>

                    <ModalLabel label="Description" />

                    <TextInput
                      expandable
                      maxLength={280}
                      multiline
                      placeholder="Optional dashboard description"
                      showCount
                      value={formState.description}
                      onChange={(event) => formState.setDescription(event.target.value)}
                    />

                    <ModalHint hint="Optional dashboard description to provide more context for viewers." />

                </ModalField>

                <ModalField>

                    {/* SWITCH ROW */}
                    <div className="dashboard-modal-switch-row">

                        {/* LABEL */}
                        <div
                          className="dashboard-modal-label"
                          style={{
                            marginBottom: 0,
                            paddingBottom: 0,
                            width: 'auto',
                          }}
                        >
                            Private
                        </div>

                        <div>
                            <Switch
                              checked={formState.isPrivate}
                              disabled={modalState.isSubmitting}
                              onChange={formState.setIsPrivate}
                            />
                        </div>

                    </div>

                </ModalField>

            </ModalForm>

            <ModalButtons>

                <Button
                  iconKey="check"
                  label={modalState.isSubmitting
                        ? (modalState.isEditMode ? 'Saving...' : 'Creating...')
                        : (modalState.isEditMode ? 'Save Changes' : 'Create Dashboard')}
                  enabled={modalState.canSubmit}
                  onClick={handlers.handleSubmit}
                />

            </ModalButtons>

        </Modal>
    );
});

// Dev Tools
DashboardModal.displayName = 'DashboardModal';

export default DashboardModal;
